import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DataNoRowOverlayComponent } from '@y42/ag-grid';
import { connectState, InputObservable } from '@y42/angular/util';
import { ErrorHandlerService } from '@y42/error-handling';
import { FilePath, GitQuery, GitService, GitTreeItem } from '@y42/git';
import { treeToArray } from '@y42/git/util';
import { fromResize } from '@y42/rxjs';
import {
  ColDef,
  GetContextMenuItems,
  GetContextMenuItemsParams,
  GetRowIdParams,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  RowClickedEvent,
  RowDataChangedEvent,
  ValueGetterParams,
} from 'ag-grid-community';
import { ColGroupDef } from 'ag-grid-community/dist/lib/entities/colDef';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  map,
  merge,
  Observable,
  Subject,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { GitPathMapper } from '../git-path-mapper';
import { GitDiffTreeItemChangeCellRendererComponent } from './git-diff-tree-item-change-cell-renderer/git-diff-tree-item-change-cell-renderer.component';
import { GitDiffTreeItemFileCellRendererComponent } from './git-diff-tree-item-file-cell-renderer/git-diff-tree-item-file-cell-renderer.component';
import { GitDiffTreeItemGroupCellRendererComponent } from './git-diff-tree-item-group-cell-renderer/git-diff-tree-item-group-cell-renderer.component';
import { GitDiffTreeItem } from './git-diff-tree.model';
import { GitDiffTreeService } from './git-diff-tree.service';

const ACTIVE_ROW_CSS_CLASS = 'active-git-tree-item';

@UntilDestroy()
@Component({
  selector: 'y42-git-diff-tree',
  templateUrl: './git-diff-tree.component.html',
  styleUrls: ['./git-diff-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GitDiffTreeComponent<T extends { path: FilePath } = { path: FilePath }> {
  constructor(
    private readonly gitService: GitService,
    private readonly gitQuery: GitQuery,
    private readonly elRef: ElementRef<HTMLElement>,
    private readonly gitPathMapper: GitPathMapper,
    private readonly router: Router,
    private readonly errorHandler: ErrorHandlerService,
    private readonly gitDiffTreeService: GitDiffTreeService,
  ) {}

  @Input()
  @InputObservable()
  changes!: T[];
  changes$!: Observable<T[] | undefined>;

  @Input() getContextMenuItems: GetContextMenuItems | undefined;

  @Input() activeItem!: T | null;

  @Input() selectedItems: T[] = [];

  @Input() allowCheckboxSelection = true;

  @Input() disableContextMenu = false;

  @Output() activeItemChange = new EventEmitter<T | null>();

  @Output() selectedItemsChange = new EventEmitter<T[]>();

  readonly srcGitFileTree$ = this.gitQuery.activeBranch$.pipe(
    filter((branch) => !!branch?.tree?.src),
    map(({ tree: { src } }) => (src?.children ? treeToArray(src.children) : [])),
  );

  private readonly gridLoading$ = new BehaviorSubject(true);

  readonly state = connectState(this, {
    rows: combineLatest([this.changes$, this.srcGitFileTree$]).pipe(
      switchMap(([changes, allFiles]) => this.prepareGridData(changes, allFiles)),
      tap((items) => {
        this.countOfFolders = Math.max(...items.map(({ folders }) => folders.length));
        // dynamically generate folder columns depends on max nested path
        this.generateColumnDefs(items);
      }),
    ),
    gridLoading: this.gridLoading$,
  });

  readonly gridOptions: GridOptions = {
    animateRows: true,
    suppressCellFocus: true,
    enableGroupEdit: true,
    suppressAggFuncInHeader: true,
    noRowsOverlayComponent: 'dataNoRowOverlayComponent',
    noRowsOverlayComponentParams: {
      text: 'No Files Found',
      body: 'Please try with another filter',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      minWidth: 100,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      filterParams: {
        suppressAndOrCondition: true,
        trimInput: false,
      },
    },

    groupSelectsChildren: true,
    groupDefaultExpanded: -1,
    groupDisplayType: 'multipleColumns',
    frameworkComponents: {
      treeItem: GitDiffTreeItemGroupCellRendererComponent,
      change: GitDiffTreeItemChangeCellRendererComponent,
      file: GitDiffTreeItemFileCellRendererComponent,
      dataNoRowOverlayComponent: DataNoRowOverlayComponent,
    },
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    getRowId: ({ data }: GetRowIdParams<T>) => data.path,
    rowClassRules: {
      [ACTIVE_ROW_CSS_CLASS]: (params) => {
        if (!this.activeItem || !params.data) {
          return false;
        }

        return params.data.path === this.activeItem.path;
      },
    },
    onSelectionChanged: (event) => {
      const selectedNodes = event.api.getSelectedNodes();
      this.selectedItems = [];
      this.selectedItemsChange.emit(selectedNodes.map((node) => node.data));
    },
    aggFuncs: {
      formattedCount: (params) => {
        const count = params.values.reduce((acc, cur) => acc + (typeof cur.value === 'number' ? cur.value : 1), 0);
        return {
          value: count,
          toString() {
            return `(${this.value})`;
          },
        };
      },
    },
    autoGroupColumnDef: {
      pinned: 'left',
      cellRenderer: 'treeItem',
      minWidth: 150,
      filter: 'agTextColumnFilter',
      headerCheckboxSelection: true,
    },
  };

  columnDefs: Array<ColDef | ColGroupDef> = [];

  private countOfFolders = 0;
  private readonly updateLayout$ = new Subject<void>();

  normalizedGetContextMenuItems = (params: GetContextMenuItemsParams) => {
    if (this.disableContextMenu) {
      return [];
    }

    const items: Array<string | MenuItemDef> = [];
    const item = params.node?.data;
    if (item) {
      const pathInfo = this.gitPathMapper.getPathInfo(item.path);
      if (pathInfo?.route) {
        items.push({
          name: 'Navigate to item',
          action: () => this.router.navigateByUrl(pathInfo.route!),
        });
      }
    }
    if (this.getContextMenuItems) {
      items.push(...this.getContextMenuItems(params));
    }
    return items;
  };

  gridReady(evt: GridReadyEvent) {
    const gridApi = evt.api;

    gridApi.sizeColumnsToFit();

    merge(fromResize(this.elRef.nativeElement).pipe(debounceTime(200)), this.updateLayout$)
      .pipe(
        tap(() => gridApi.sizeColumnsToFit()),
        untilDestroyed(this),
      )
      .subscribe();

    timer(300)
      .pipe(
        take(1),
        tap(() => this.gridLoading$.next(false)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  updateLayout() {
    this.updateLayout$.next();
  }

  onRowDataChanged(event: RowDataChangedEvent) {
    if (this.selectedItems?.length) {
      event.api.forEachNode((node) => {
        if (node.data && this.selectedItems.some(({ path }) => path === node.data.path)) {
          node.setSelected(true);
        }
      });
    }
  }

  onRowClicked(event: RowClickedEvent) {
    if (!event.data) {
      return;
    }
    const activeRowElements = Array.from(
      this.elRef.nativeElement.querySelectorAll(`.ag-body-viewport .${ACTIVE_ROW_CSS_CLASS}`),
    );
    activeRowElements.forEach((elem) => elem.classList.remove(ACTIVE_ROW_CSS_CLASS));
    const clickedRowElements = Array.from(
      this.elRef.nativeElement.querySelectorAll(`.ag-body-viewport [row-index="${event.rowIndex}"]`),
    );
    clickedRowElements.forEach((elem) => elem.classList.add(ACTIVE_ROW_CSS_CLASS));

    this.activeItemChange.emit(event.data);
  }

  private readonly generateColumnDefs = (rows?: GitDiffTreeItem[]): void => {
    const hasStatus = (rows || this.state.rows || []).some((item) => 'status' in item);
    this.columnDefs = [
      {
        checkboxSelection: this.allowCheckboxSelection,
        headerCheckboxSelection: this.allowCheckboxSelection,
        field: 'file',
        headerName: 'File',
        cellRenderer: 'file',
        aggFunc: 'formattedCount',
        filter: 'agTextColumnFilter',
        cellClass: 'font-semibold',
      },
      ...(hasStatus
        ? [
            {
              field: 'status',
              headerName: 'Status',
              cellRenderer: 'change',
              sortable: false,
              filter: 'agSetColumnFilter',
              maxWidth: 130,
            },
          ]
        : []),
      {
        field: 'product',
        filter: 'agSetColumnFilter',
        cellRenderer: 'treeItem',
        width: 180,
        enableRowGroup: true,
        comparator: (valueA, valueB) => {
          if (typeof valueA === 'string' && typeof valueB === 'string') {
            valueA = valueA.replace(/^\d+-/, '');
            valueB = valueB.replace(/^\d+-/, '');

            if (valueA < valueB) {
              return -1;
            }
            if (valueA > valueB) {
              return 1;
            }
          }
          return 0;
        },
      },
      {
        field: 'typeDisplayName',
        width: 140,
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
        cellRenderer: 'treeItem',
        valueGetter: (params: ValueGetterParams<GitDiffTreeItem>) => {
          const item = params.data;
          return item?.typeDisplayName;
        },
      },
      ...Array.from({ length: this.countOfFolders }, (_, index) => ({
        field: `level${index}`,
        headerName: `Folder ${index + 1}`,
        width: 120,
        valueGetter: (params: ValueGetterParams) => {
          const item = params.data as GitDiffTreeItem;
          return item?.folders[index];
        },
        filter: 'agTextColumnFilter',
      })),
    ];
  };

  private async prepareGridData(changes: T[] | undefined, allFiles: GitTreeItem[]): Promise<GitDiffTreeItem[]> {
    return Promise.all(
      (changes || []).map(async (change) => {
        const folders = change.path.replace('src/', '').split('/').slice(0, -1);
        const fileName = change.path.split('/').reverse()[0] ?? '';
        const entityType = await this.getEntryTypeFromFileChange(change, allFiles);

        const treeItem: GitDiffTreeItem = {
          ...change,
          file: fileName.split('.')[0] ?? '',
          fileName,
          product: folders?.[0] ?? '',
          typeDisplayName: entityType?.typeDisplayName || null,
          typeLogoUrl: entityType?.typeLogoUrl || null,
          folders,
        };
        return treeItem;
      }),
    );
  }

  private async getEntryTypeFromFileChange(
    fileChange: T,
    allFiles: GitTreeItem[],
  ): Promise<{
    typeDisplayName: string;
    typeLogoUrl: string;
  } | null> {
    const typePath = fileChange.path.split('/').slice(0, 3).join('/');
    const typePathWithSubFeature = fileChange.path.split('/').slice(0, 4).join('/');
    const settingsFile = allFiles.find(
      (file) => file.path === `${typePath}/settings.json` || file.path === `${typePathWithSubFeature}/settings.json`,
    );
    if (!settingsFile) {
      return null;
    }
    if (settingsFile) {
      try {
        const settings = await this.gitService.readJSON<{ $schema: string }>(settingsFile.path);

        if (!settings) {
          return null;
        }

        const typeDisplayName = this.gitDiffTreeService.getTypeDisplayName(settings.$schema);
        const typeLogoUrl = this.gitDiffTreeService.getTypeLogo(settings.$schema);

        if (!typeDisplayName || !typeLogoUrl) {
          return null;
        }

        return { typeDisplayName, typeLogoUrl };
      } catch (error) {
        this.errorHandler.handleError(error);
        return null;
      }
    }
    return null;
  }
}
